import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import SiteBreadcrumb from '../../components/layout/SiteBreadcrumb';
import Config from '../../../helpers/Config';
import { getLanguageCodeFromSession } from '../../../helpers/CommonHelper';
import { translateText } from '../../../helpers/LanguageHelper';

const faqData = [
    { topic: 'Delivery', question: 'What Shipping Methods are Available?', answer: 'We ship through trusted courier partners. Estimated delivery time is 1–4 days for main areas and 4–8 days for interior areas. Delivery times may vary depending on your location and courier availability.' },
    { topic: 'Ordering', question: 'What payment methods can I use?', answer: 'We accept UPI payments using the payment QR code after your order is confirmed. Please complete payment before your order is shipped. Cash on delivery (COD) is not available.' },
    { topic: 'Products', question: 'Why Choose UKLButter?', answer: 'Enjoy the authentic taste of Uthukuli with our 99% pure, natural butter, sourced directly from Uthukuli and made without preservatives.' },
    { topic: 'Products', question: 'Health Benefits of Uthukuli Butter?', answer: 'Rich in vitamins A, D, E, and K, our butter supports healthy skin, boosts energy, and enhances immunity when consumed in moderation.' },
    { topic: 'Products', question: 'Is Uthukuli Cow Butter made from cow’s milk?', answer: 'Yes, we use 100% fresh cow’s milk(no mixed milk) from own farms.' },
    { topic: 'Storage', question: 'Packaging & Storage', answer: 'Our butter is hygienically packed in food-grade containers to preserve its freshness and quality. Once you receive it, place the unopened container in the freezer for 30 minutes before opening. This helps the butter firm up and reduces the chance of spills if it has softened during delivery.' },
    { topic: 'Ordering', question: 'Can I return my order?', answer: 'We do not accept returns because our products are consumable food items. This helps us maintain food safety, hygiene, and quality. If you have any concerns about your order, please contact us for assistance.' },
    { topic: 'Delivery', question: 'How can I help avoid delivery delays?', answer: 'Delivery may be delayed by an incomplete or incorrect address, unanswered calls, or requests to reschedule. After placing your order, please keep your phone reachable and answer calls from our team or delivery partner to help ensure a smooth delivery. If you need to reschedule, please coordinate with the delivery partner. If you would like your order handed to gate security, please inform both the delivery partner and your security staff in advance, and arrange to collect it promptly. Thank you for helping us deliver your order smoothly.' },
    { topic: 'Delivery', question: 'Will I need to collect my order from a courier branch?', answer: 'Doorstep delivery may not be available in some remote or interior areas. In such cases, please collect your order from the nearby courier branch designated by the delivery partner once it arrives. Please coordinate with the delivery partner to confirm the branch location and collection timings, and collect your order promptly to help maintain its freshness.' },
    { topic: 'Delivery', question: 'Where do you deliver ghee and butter?', answer: 'Our ghee is available for delivery across India. Butter delivery is available in major cities across South India and all locations within Tamil Nadu.' },
];

const topics = ['All topics', 'Products', 'Ordering', 'Delivery', 'Storage'];

const FaqPage = () => {
    const [search, setSearch] = useState('');
    const [topic, setTopic] = useState('All topics');
    const [openQuestion, setOpenQuestion] = useState(0);
    const languageCode = getLanguageCodeFromSession();
    const query = search.trim().toLowerCase();
    const visibleQuestions = faqData
        .map((faq, index) => ({ ...faq, index, translatedQuestion: translateText(faq.question), translatedAnswer: translateText(faq.answer) }))
        .filter(faq => (topic === 'All topics' || faq.topic === topic) &&
            (!query || `${faq.question} ${faq.answer} ${faq.translatedQuestion} ${faq.translatedAnswer} ${faq.topic}`.toLowerCase().includes(query)));

    const resetFilters = () => {
        setSearch('');
        setTopic('All topics');
    };

    return (
        <>
            <Helmet>
                <title>{Config.SITE_TTILE} - Frequently Asked Questions</title>
                <meta name="description" content="Find answers about UKL Butter products, ordering, delivery and storage." />
            </Helmet>
            <SiteBreadcrumb title="FAQ" parent="Home" />

            <main className="ukl-faq-page">
                <div className="container">
                    <header className="ukl-faq-hero">
                        <span className="ukl-faq-eyebrow">Help centre</span>
                        <h1>Frequently Asked Questions</h1>
                        <p>Find quick answers about our butter, ordering, delivery and storage.</p>
                        <label className="ukl-faq-search">
                            <i className="fa fa-search" aria-hidden="true" />
                            <input type="search" value={search} onChange={event => setSearch(event.target.value)}
                                placeholder="Search questions or answers" aria-label="Search frequently asked questions" />
                        </label>
                    </header>

                    <div className="ukl-faq-layout">
                        <aside className="ukl-faq-sidebar">
                            <div className="ukl-faq-guide">
                                <span className="ukl-faq-guide__icon"><i className="fa fa-lightbulb-o" aria-hidden="true" /></span>
                                <h2>Need a quick answer?</h2>
                                <p>Search above or choose a topic to find what you need.</p>
                                <ul>
                                    <li>Butter and ingredients</li>
                                    <li>Orders and payment</li>
                                    <li>Shipping and storage</li>
                                </ul>
                                <Link to={`/${languageCode}/contact-us`}>Contact us <i className="fa fa-arrow-right" aria-hidden="true" /></Link>
                            </div>
                        </aside>

                        <section aria-label="FAQ answers">
                            <div className="ukl-faq-topics" aria-label="Filter questions by topic">
                                {topics.map(item => (
                                    <button key={item} type="button" aria-pressed={topic === item} onClick={() => setTopic(item)}>{item}</button>
                                ))}
                            </div>
                            <p className="ukl-faq-count" aria-live="polite">
                                {visibleQuestions.length} {visibleQuestions.length === 1 ? 'question' : 'questions'} found
                            </p>
                            {visibleQuestions.length ? visibleQuestions.map(faq => (
                                <article key={faq.index} className={`ukl-faq-item${openQuestion === faq.index ? ' is-open' : ''}`}>
                                    <h2>
                                        <button type="button" aria-expanded={openQuestion === faq.index}
                                            aria-controls={`faq-answer-${faq.index}`}
                                            onClick={() => setOpenQuestion(openQuestion === faq.index ? null : faq.index)}>
                                            {faq.translatedQuestion}
                                            <i className={`fa fa-${openQuestion === faq.index ? 'minus' : 'plus'}`} aria-hidden="true" />
                                        </button>
                                    </h2>
                                    {openQuestion === faq.index && <div className="ukl-faq-answer" id={`faq-answer-${faq.index}`}><p>{faq.translatedAnswer}</p></div>}
                                </article>
                            )) : (
                                <div className="ukl-faq-empty">
                                    <h2>No matching questions</h2>
                                    <p>Try another search or browse all topics.</p>
                                    <button type="button" onClick={resetFilters}>Show all questions</button>
                                </div>
                            )}
                        </section>
                    </div>

                    <div className="ukl-order-faq">
                        <span><i className="fa fa-comments-o" aria-hidden="true" /></span>
                        <div>
                            <h2>Still have a question?</h2>
                            <p>Get in touch and we will help with your order or product question.</p>
                        </div>
                        <Link to={`/${languageCode}/contact-us`}>Contact us <i className="fa fa-arrow-right" aria-hidden="true" /></Link>
                    </div>
                </div>
            </main>
        </>
    );
};

export default FaqPage;
